import { motion } from 'framer-motion';
import { ReactNode } from 'react';
import { useAppSettings } from '../../contexts/AppSettingsContext';

interface FadeInProps {
	children: ReactNode;
	delay?: number;
	duration?: number;
	direction?: 'up' | 'down' | 'left' | 'right' | 'none';
	distance?: number;
}

function getInitial(direction: FadeInProps['direction'], distance: number) {
	switch (direction) {
		case 'up':    return { opacity: 0, y: distance };
		case 'down':  return { opacity: 0, y: -distance };
		case 'left':  return { opacity: 0, x: distance };
		case 'right': return { opacity: 0, x: -distance };
		default:      return { opacity: 0 };
	}
}

function getExit(direction: FadeInProps['direction']) {
	switch (direction) {
		case 'up':    return { opacity: 0, y: -4 };
		case 'down':  return { opacity: 0, y: 4 };
		case 'left':  return { opacity: 0, x: -4 };
		case 'right': return { opacity: 0, x: 4 };
		default:      return { opacity: 0 };
	}
}

export default function FadeIn({
	children,
	delay = 0,
	duration = 0.4,
	direction = 'up',
	distance = 12,
}: FadeInProps) {
	const { shouldAnimate, durationMultiplier } = useAppSettings();

	if (!shouldAnimate) return <>{children}</>;

	const initial = getInitial(direction, distance);
	const exit = getExit(direction);

	return (
		<motion.div
			initial={initial}
			animate={{ opacity: 1, x: 0, y: 0 }}
			exit={exit}
			transition={{ duration: duration * durationMultiplier, delay: delay * durationMultiplier, ease: [0.16, 1, 0.3, 1] }}
		>
			{children}
		</motion.div>
	);
}
